import {TableRow, TableCell, ControlledInput, Button} from '@hm/fundamental'
import {mdiDeleteOutline} from '@mdi/js'
import {useEffect, useCallback} from 'react'
import {Controller, UseFieldArrayRemove, useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {DrillingZone} from '@refra/modules/refra-planning/types'

type DrillingZonesTableRowProps = {
  drillingZone: DrillingZone
  index: number
  remove: UseFieldArrayRemove
  numberOfRows: number
  kilnLength: number
}

export const DrillingZonesTableRow = ({
  drillingZone,
  index,
  remove,
  numberOfRows,
  kilnLength
}: DrillingZonesTableRowProps) => {
  const {t} = useTranslation()
  const {control, watch, setValue} = useFormContext<{drillingZones: DrillingZone[]}>()

  const isLastRow = index === numberOfRows

  const removeRow = useCallback(() => {
    remove(index)
    if (isLastRow) {
      setValue(`drillingZones.${index - 1}.toMeter`, kilnLength)
    }
  }, [index, isLastRow, kilnLength, remove, setValue])

  const parseNumericValue = useCallback(
    (value: number | string) => {
      if (value === '' || value === null || value === undefined) {
        return {parsedValue: null, error: t('error.required')}
      }

      const parsedValue = Number(value)
      if (Number.isNaN(parsedValue)) {
        return {parsedValue: null, error: t('error.enterValidInput')}
      }

      return {parsedValue, error: null}
    },
    [t]
  )

  const validateToMeter = useCallback(
    (value: number | string) => {
      const {parsedValue, error} = parseNumericValue(value)
      if (error) return error
      if (parsedValue === null) return t('error.enterValidInput')

      const fromMeter = Number(watch(`drillingZones.${index}.fromMeter`))

      if (parsedValue <= 0) return t('error.lessThanOrEqualZero')
      if (parsedValue >= kilnLength) return t('error.moreThanKilnLength')
      if (parsedValue <= fromMeter) return t('error.lessThanStart')

      return true
    },
    [index, kilnLength, parseNumericValue, t, watch]
  )

  const validateThreshold = useCallback(
    (value: number | string) => {
      const {parsedValue, error} = parseNumericValue(value)
      if (error) return error
      if (parsedValue === null || parsedValue <= 0) return t('error.minExclusiveValue', {value: 0})
      return true
    },
    [parseNumericValue, t]
  )

  const watchEnd = watch(`drillingZones.${index}.toMeter`)

  useEffect(() => {
    if (!isLastRow) {
      setValue(`drillingZones.${index + 1}.fromMeter`, Number(watchEnd))
    }
  }, [index, isLastRow, setValue, watchEnd])

  return (
    <TableRow data-cy="drilling_zones_table_row">
      <TableCell className="w-[55%] text-center" data-cy="drilling_zones_name">
        <Controller
          name={`drillingZones.${index}.name`}
          control={control}
          rules={{required: t('error.required')}}
          render={({field, fieldState: {error}}) => (
            <ControlledInput
              {...field}
              label={''}
              allowFullScreen
              error={error?.message}
              isTouched={!!error?.message}
            />
          )}
        />
      </TableCell>
      <TableCell className="w-[11%] text-center" data-cy="drilling_zones_threshold">
        <Controller
          name={`drillingZones.${index}.threshold`}
          control={control}
          rules={{validate: {isPositiveValue: validateThreshold}}}
          render={({field, fieldState: {error}}) => (
            <ControlledInput
              {...field}
              value={field.value.toString()}
              label={''}
              error={error?.message}
              isTouched={!!error?.message}
              step={0.1}
              type={'number'}
            />
          )}
        />
      </TableCell>
      <TableCell className="w-[11%] text-center" data-cy="drilling_zones_start">
        {watch(`drillingZones.${index}.fromMeter`)}
      </TableCell>
      <TableCell className="w-[11%] text-center" data-cy="drilling_zones_end">
        {isLastRow ? (
          <> {drillingZone.toMeter}</>
        ) : (
          <Controller
            name={`drillingZones.${index}.toMeter`}
            control={control}
            rules={{validate: {isInBetweenValues: validateToMeter}}}
            render={({field, fieldState: {error}}) => (
              <ControlledInput
                {...field}
                value={field.value.toString()}
                label={''}
                error={error?.message}
                isTouched={!!error?.message}
                min={watch(`drillingZones.${index}.fromMeter`)}
                step={0.1}
                type={'number'}
              />
            )}
          />
        )}
      </TableCell>
      <TableCell>
        {index !== 0 && (
          <Button
            icon={mdiDeleteOutline}
            variant="negative"
            type="button"
            onClick={removeRow}
            data-cy="drilling_zones_delete_btn"
          />
        )}
      </TableCell>
    </TableRow>
  )
}
